import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../../components/common/IconWrapper';
import {
  HiCloud,
  HiServer,
  HiShieldCheck,
  HiChartBar,
  HiBolt,
  HiEye,
  HiDocumentText,
  HiCodeBracket,
  HiRocketLaunch,
  HiCheckCircle
} from 'react-icons/hi2';
import { HiDatabase } from 'react-icons/hi';

const heroImage = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=80';

const heroStats = [
  { label: 'GCP Services', value: '150+' },
  { label: 'Cost Savings', value: '35%' },
  { label: 'Uptime', value: '99.9%' }
];

const capabilities = [
  {
    title: 'GCP Infrastructure',
    description: 'Design and deploy scalable GCP infrastructure with Compute Engine, Cloud Storage, and networking.',
    icon: HiServer
  },
  {
    title: 'BigQuery & Analytics',
    description: 'Implement BigQuery data warehouses, Dataflow pipelines, and analytics solutions.',
    icon: HiDatabase
  },
  {
    title: 'GCP AI & ML',
    description: 'Leverage GCP AI services, Vertex AI, and machine learning platforms for intelligent solutions.',
    icon: HiBolt
  },
  {
    title: 'GCP Security',
    description: 'Implement GCP security best practices, IAM, and compliance frameworks.',
    icon: HiShieldCheck
  },
  {
    title: 'Cloud Build & DevOps',
    description: 'Set up CI/CD pipelines, infrastructure as code, and automated deployments on GCP.',
    icon: HiCodeBracket
  },
  {
    title: 'Cost Optimization',
    description: 'Optimize GCP costs with committed use discounts, sustained use discounts, and FinOps.',
    icon: HiChartBar
  }
];

const processSteps = [
  {
    title: 'Assessment & Planning',
    description: 'Assess current infrastructure, plan GCP migration, and define architecture.',
    icon: HiEye
  },
  {
    title: 'Architecture Design',
    description: 'Design GCP architecture with best practices for scalability, security, and cost.',
    icon: HiDocumentText
  },
  {
    title: 'Implementation',
    description: 'Deploy GCP resources, migrate workloads, and configure services.',
    icon: HiCodeBracket
  },
  {
    title: 'Optimization & Support',
    description: 'Optimize performance, implement monitoring, and provide ongoing support.',
    icon: HiRocketLaunch
  }
];

const technologies = [
  'Compute Engine',
  'Cloud Run',
  'Cloud Functions',
  'Google Kubernetes Engine',
  'Cloud SQL',
  'BigQuery',
  'Cloud Storage',
  'Dataflow',
  'Vertex AI',
  'Cloud Build',
  'Cloud Monitoring',
  'Cloud Security Command Center',
  'Terraform',
  'Deployment Manager'
];

const benefits = [
  'Scalable Infrastructure',
  'Advanced Analytics',
  'Cost Optimization',
  'Global Network',
  'AI/ML Integration',
  'Enterprise Security'
];

const CloudGCPPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img src={heroImage} alt="GCP Cloud Services" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <IconWrapper icon={HiCloud} className="w-5 h-5 text-blue-400 mr-2" />
                <span className="text-sm font-medium text-white">Cloud Data Services</span>
              </motion.div>

              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                GCP
              </motion.h1>

              <motion.p
                className="text-xl text-white/85 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Comprehensive Google Cloud Platform services for infrastructure, data analytics, AI/ML, and application deployment.
              </motion.p>

              <motion.div
                className="flex flex-wrap gap-4 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                {heroStats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/20">
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative w-full h-80 lg:h-[440px] rounded-3xl overflow-hidden shadow-2xl border border-white/30 bg-white/10 backdrop-blur-sm">
                <img src={heroImage} alt="GCP Cloud" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-slate-900/40" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Capabilities</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <IconWrapper icon={capability.icon} className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{capability.title}</h3>
                <p className="text-slate-600 dark:text-slate-300">{capability.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Process</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 font-semibold flex items-center justify-center mb-4">
                  {index + 1}
                </div>
                <IconWrapper icon={step.icon} className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{step.title}</h3>
                <p className="text-slate-600 dark:text-slate-300 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Technologies</h3>
              <div className="flex flex-wrap gap-3">
                {technologies.map(tech => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 rounded-full text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Key Benefits</h3>
              <ul className="space-y-3">
                {benefits.map(benefit => (
                  <li key={benefit} className="flex items-start text-slate-600 dark:text-slate-300">
                    <IconWrapper icon={HiCheckCircle} className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="relative p-10 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden bg-white dark:bg-slate-950"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-100 dark:from-slate-900 dark:to-slate-950" />
            <div className="relative z-10">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Ready to Leverage GCP?
              </motion.h2>
              <motion.p
                className="text-lg text-slate-600 dark:text-slate-300 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Let's discuss how we can help you build scalable solutions on Google Cloud Platform.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.button
                  className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold text-lg hover:bg-blue-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Get Started
                </motion.button>
                <motion.button
                  className="px-8 py-4 bg-transparent border-2 border-blue-600 dark:border-slate-700 text-blue-600 dark:text-white rounded-full font-semibold text-lg hover:bg-blue-50 dark:hover:bg-slate-900 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Schedule Consultation
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CloudGCPPage;

